import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { getAdminStats, getAdminSetupStatus } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, FolderOpen, ShoppingCart, Euro, TrendingUp, AlertTriangle, CheckCircle2, ExternalLink, RefreshCw, Loader2, Map } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SetupBanner = ({ service, data }) => {
  if (data?.configured) return null;

  const configs = {
    stripe: {
      title: 'Paiements en ligne (Stripe)',
      color: 'amber',
      steps: [
        { num: 1, text: 'Créez un compte sur', link: 'https://stripe.com', linkText: 'stripe.com' },
        { num: 2, text: 'Dans Dashboard → Développeurs → Clés API, copiez votre clé secrète (commence par sk_live_...)' },
        { num: 3, text: 'Ouvrez le fichier /app/backend/.env et remplacez la ligne STRIPE_API_KEY= par votre vraie clé' },
        { num: 4, text: 'Redémarrez le serveur : sudo supervisorctl restart backend' },
      ],
      note: 'En mode test, utilisez une clé sk_test_... Stripe fournit des cartes de test.',
    },
    resend: {
      title: 'Emails automatiques (Resend)',
      color: 'blue',
      steps: [
        { num: 1, text: 'Créez un compte sur', link: 'https://resend.com', linkText: 'resend.com' },
        { num: 2, text: 'Dans Dashboard → API Keys → Create API Key, copiez votre clé (commence par re_...)' },
        { num: 3, text: 'Ouvrez /app/backend/.env et renseignez RESEND_API_KEY=re_votre_cle' },
        { num: 4, text: 'Vérifiez que SENDER_EMAIL et OWNER_EMAIL sont corrects dans le .env' },
        { num: 5, text: 'Redémarrez : sudo supervisorctl restart backend' },
      ],
      note: 'Gratuit jusqu\'à 3 000 emails/mois. Les emails incluent : notification nouvelle commande + confirmation client.',
    },
  };

  const cfg = configs[service];
  const colorClasses = {
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', title: 'text-amber-800', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', title: 'text-blue-800', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  }[cfg.color];

  return (
    <div className={`${colorClasses.bg} border ${colorClasses.border} rounded-xl p-5 mb-4`} data-testid={`setup-${service}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 text-${cfg.color}-500`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className={`font-semibold ${colorClasses.title}`}>{cfg.title} — Configuration requise</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${colorClasses.badge}`}>Non configuré</span>
          </div>
          <ol className="space-y-1.5 mb-3">
            {cfg.steps.map(step => (
              <li key={step.num} className="flex items-start gap-2 text-sm text-stone-700">
                <span className={`w-5 h-5 ${colorClasses.dot} text-white text-xs rounded-full flex items-center justify-center shrink-0 mt-0.5`}>{step.num}</span>
                <span>
                  {step.text}
                  {step.link && (
                    <a href={step.link} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-primary font-medium hover:underline ml-1">
                      {step.linkText} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </span>
              </li>
            ))}
          </ol>
          <p className="text-xs text-stone-500 italic">{cfg.note}</p>
        </div>
      </div>
    </div>
  );
};

function SitemapRegenerateButton() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleRegenerate = async () => {
    setLoading(true);
    try {
      const res = await api.post('/admin/sitemap/regenerate');
      setResult(res.data.count);
      toast.success(`Sitemap régénéré — ${res.data.count} URLs`);
    } catch {
      toast.error('Erreur lors de la régénération');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleRegenerate}
      disabled={loading}
      className="w-full"
      data-testid="regenerate-sitemap-btn"
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
      {result ? `Régénéré (${result} URLs)` : 'Régénérer le sitemap'}
    </Button>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem('tapisstore_admin_token')) { setLoading(false); return; }
    const fetchAll = async () => {
      try {
        const [statsRes, setupRes] = await Promise.all([
          getAdminStats(),
          getAdminSetupStatus().catch(() => ({ data: null }))
        ]);
        setStats(statsRes.data);
        setSetup(setupRes.data);
      } catch (err) {
        const status = err.response?.status;
        if (status !== 401 && status !== 403) console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const statCards = [
    { title: 'Produits', value: stats?.total_products || 0, icon: Package, color: 'text-blue-600', bgColor: 'bg-blue-100' },
    { title: 'Catégories', value: stats?.total_categories || 0, icon: FolderOpen, color: 'text-green-600', bgColor: 'bg-green-100' },
    { title: 'Commandes', value: stats?.total_orders || 0, icon: ShoppingCart, color: 'text-purple-600', bgColor: 'bg-purple-100' },
    { title: 'Chiffre d\'affaires', value: `${(stats?.total_revenue || 0).toFixed(2)} €`, icon: Euro, color: 'text-amber-600', bgColor: 'bg-amber-100' },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-semibold">Tableau de bord</h1>
          <p className="text-muted-foreground mt-1">Vue d'ensemble de votre boutique Villa Marceau</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title} data-testid={`stat-${stat.title.toLowerCase()}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                      <p className="text-2xl font-semibold">{loading ? '...' : stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recent Orders + SEO tools */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Sitemap */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading flex items-center gap-2 text-base">
              <Map className="w-4 h-4 text-primary" />
              Sitemap SEO
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Régénérez le sitemap après avoir ajouté des produits ou catégories pour que Google indexe vos nouvelles pages.
            </p>
            <SitemapRegenerateButton />
            <a
              href="/sitemap.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Voir le sitemap actuel
            </a>
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Commandes récentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-16 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : stats?.recent_orders?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_orders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg" data-testid={`recent-order-${order.id}`}>
                    <div>
                      <p className="font-medium">{order.customer_name}</p>
                      <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-primary">{order.total.toFixed(2)} €</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {order.payment_status === 'paid' ? 'Payé' : 'En attente'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">Aucune commande pour le moment</p>
            )}
          </CardContent>
        </Card>

        </div>
      </div>
    </AdminLayout>
  );
}
